/**
 * Live-data hooks. Everything is `onSnapshot`-based (no polling), so a document
 * or query result updates the moment a host writes it — the whole UI is a
 * projection of the two Firestore databases.
 *
 * `useInstructionAction` is the one hook that writes: it pairs the create with
 * the subscription that reports back on it, because in this UI those are always
 * used together (see `components/ActionCard.tsx`).
 */
import { useEffect, useState } from 'react';
import {
    collection,
    doc,
    limit,
    onSnapshot,
    orderBy,
    query,
    where,
    type Firestore,
    type Query,
} from 'firebase/firestore';
import { getBytes, ref as storageRef } from 'firebase/storage';
import {
    COLLECTION,
    DEPLOY_RECORD_FIELD,
    INSTRUCTION_FIELD,
    MAIN_DOC,
    SUBCOLLECTION,
    componentDocId,
    hostInstance,
    isInflightStatus,
    type ComponentDoc,
    type ComponentHistoryDoc,
    type HostRole,
    type InstructionDoc,
    type InstructionKind,
    type HostDoc,
    type Phase,
    type PrepareReleaseDoc,
    type ReleaseDoc,
    type UnreleasedDoc,
} from '@finvari/deployer';
import { useOperator } from './auth';
import { dbDefault, dbForRole, dbPrepare, storage } from './firebase';
import { componentRows, deployProgress, type ComponentRow, type DeployProgress } from './lib/componentRows';
import { createInstruction } from './lib/createInstruction';
import {
    activeDeployRelease,
    activePrepareRelease,
    homePhase,
    homeSourcesLoading,
    type DeployRelease,
    type PrepareRelease,
} from './lib/homeMode';
import { hostView } from './lib/hostStatus';
import { systemView, type SystemView } from './lib/releaseSystem';
import {
    describeActivity,
    INFLIGHT_DISABLED_REASON,
    latestForAction,
    type ActivityLine,
    type InstructionRow,
} from './lib/instructionActivity';
import { storageErrorText } from './lib/storageError';

/** How many recent instructions the activity views look at per host. */
export const ACTIVITY_WINDOW = 25;

/**
 * How many of a release's own instructions the deploying view looks at. Larger than
 * the per-host window because it is spread over about twenty components: a release
 * with a few failed retries would otherwise push the earliest components' rows out
 * of the window and leave them looking as if nothing had ever been asked of them.
 */
export const RELEASE_ACTIVITY_WINDOW = 200;

/** For a caption counting in seconds: heartbeat age, boot progress, a running duration. */
export const LIVE_TICK_MS = 1_000;

/**
 * For a view whose only clock-derived text is a coarse "2m ago" (`agoTextCoarse`).
 * A second-by-second tick would re-render the whole card to produce identical
 * markup; a 15 s one keeps the caption honest to its own resolution.
 */
export const CAPTION_TICK_MS = 15_000;

/**
 * The current time, re-read every `intervalMs`. Any view that derives state from
 * `Date.now()` — heartbeat age, countdowns, running durations — must use this
 * rather than calling `Date.now()` in render, or it only updates when some
 * unrelated snapshot happens to re-render it. Pick the interval from the finest
 * unit actually on screen: a view can switch between the two as its content does.
 */
export function useNow(intervalMs = LIVE_TICK_MS): number {
    const [now, setNow] = useState(() => Date.now());
    useEffect(() => {
        const timer = setInterval(() => setNow(Date.now()), intervalMs);
        return () => clearInterval(timer);
    }, [intervalMs]);
    return now;
}

/**
 * A live subscription's value together with whether it has heard from the server
 * yet.
 *
 * The two have to be separate because Firestore reports "this document does not
 * exist" and "no snapshot has arrived yet" as the same absent value. A view that
 * reads only the value therefore states as fact something it has not learned —
 * a host card offering **Start** for a host that is already running, a page
 * announcing "no release in progress" while one is open, a list saying "none yet"
 * before it has looked. Every one of those invites a click the operator did not
 * need to make, and the slower the connection the longer it is on screen.
 *
 * So: `loading` means `value` is a placeholder, not an answer. Anything that
 * offers an action or asserts a state must check it.
 */
export interface Loadable<T> {
    value: T;
    loading: boolean;
}

/** Subscribe to a single document. */
export function useDoc<T>(db: Firestore, path: string): Loadable<T | undefined> {
    const [state, setState] = useState<Loadable<T | undefined>>({ value: undefined, loading: true });
    useEffect(() => {
        // A new path is a new question: go back to loading rather than answering it
        // with the previous path's document.
        setState({ value: undefined, loading: true });
        return onSnapshot(doc(db, path), (snap) =>
            setState({ value: snap.exists() ? (snap.data() as T) : undefined, loading: false }),
        );
    }, [db, path]);
    return state;
}

/**
 * Subscribe to a query built from primitive dependencies. The builder is called
 * inside the effect so the `Query` object is not a render-identity dependency
 * (which would resubscribe every render); the `deps` are the primitives it
 * closes over.
 */
export function useQuery<T>(build: () => Query, deps: unknown[]): Loadable<Array<T & { id: string }>> {
    const [state, setState] = useState<Loadable<Array<T & { id: string }>>>({ value: [], loading: true });
    useEffect(() => {
        setState({ value: [], loading: true });
        return onSnapshot(build(), (snap) =>
            setState({ value: snap.docs.map((d) => ({ id: d.id, ...(d.data() as T) })), loading: false }),
        );
    }, deps);
    return state;
}

/** The host document for a role, from that role's database. */
export function useHost(role: HostRole): Loadable<HostDoc | undefined> {
    return useDoc<HostDoc>(dbForRole(role), `${COLLECTION.hosts}/${role}`);
}

/**
 * The `max` newest instructions for a host.
 *
 * `max` is required rather than optional because nothing deletes an instruction
 * document — a release writes roughly twenty-five of them and they accumulate
 * forever, so an unbounded read here would grow without limit. The bound has to
 * be in the query: sorting in the browser instead would mean downloading the
 * whole history to find out which rows are newest, which is the cost we are
 * avoiding. That is also why the `host` + `createdAt` composite index is
 * declared in both index files.
 *
 * The `host` filter looks redundant against production — each host's
 * instructions live in its own database and the rules enforce the field on write
 * — but it is load-bearing under the emulator, where both roles collapse onto
 * `(default)` and the two hosts' instructions genuinely share a collection.
 */
export function useInstructions(role: HostRole, max: number): Loadable<Array<InstructionDoc & { id: string }>> {
    return useQuery<InstructionDoc>(
        () =>
            query(
                collection(dbForRole(role), COLLECTION.instructions),
                where(INSTRUCTION_FIELD.host, '==', role),
                orderBy(INSTRUCTION_FIELD.createdAt, 'desc'),
                limit(max),
            ),
        [role, max],
    );
}

/**
 * The recent instructions for a host, for the activity that cards show next to
 * their buttons ("queued", "running for 40s", "last run failed: …"). One shared
 * window size so every card on a page subscribes to the same query and the SDK
 * multiplexes them onto one listener.
 */
export function useRecentInstructions(role: HostRole): Loadable<Array<InstructionDoc & { id: string }>> {
    return useInstructions(role, ACTIVITY_WINDOW);
}

/**
 * All preparation-release documents from the `prepare` database. There is at most
 * one active at a time; `activePrepareRelease` selects it. Subscribed live so the
 * home page re-derives its mode the moment the prepare host writes a step result.
 */
export function usePrepareReleases(): Loadable<Array<PrepareReleaseDoc & { id: string }>> {
    return useQuery<PrepareReleaseDoc>(() => collection(dbPrepare, COLLECTION.releases), []);
}

/**
 * All release documents from `(default)` — the deploy half's projection.
 *
 * Unbounded, like `usePrepareReleases`: a release document is written once per
 * release and holds a plan of about twenty rows, so the collection grows at a few
 * kilobytes per release. `/releases` lists them all, and the home page needs to know
 * whether *any* is open, which a `limit` would not answer.
 */
export function useDefaultReleases(): Loadable<Array<ReleaseDoc & { id: string }>> {
    return useQuery<ReleaseDoc>(() => collection(dbDefault, COLLECTION.releases), []);
}

/** One release from `(default)`, for the pages addressed by version. */
export function useRelease(version: string): Loadable<ReleaseDoc | undefined> {
    return useDoc<ReleaseDoc>(dbDefault, `${COLLECTION.releases}/${version}`);
}

/**
 * Every component row. The whole registry, not the changed ones: the table shows
 * what needs nothing as well as what does, because "receipts is not in this release"
 * is the answer to a question an operator asks about it.
 */
export function useComponents(): Loadable<Array<ComponentDoc & { id: string }>> {
    return useQuery<ComponentDoc>(() => collection(dbDefault, COLLECTION.components), []);
}

/**
 * One component's deploy history, newest release first. Keyed by `componentDocId`
 * because a name can contain `/` and a document id cannot.
 */
export function useComponentHistory(name: string | undefined): Loadable<Array<ComponentHistoryDoc & { id: string }>> {
    return useQuery<ComponentHistoryDoc>(
        () =>
            query(
                collection(dbDefault, COLLECTION.components, componentDocId(name ?? '-'), SUBCOLLECTION.history),
                orderBy(DEPLOY_RECORD_FIELD.at, 'desc'),
            ),
        [name],
    );
}

/**
 * The release host's instructions for one release, newest first.
 *
 * Filtered to the release rather than reusing `useRecentInstructions`, because the
 * component table keys each row's "compiling"/"deploying"/"failed" state off these
 * and a fixed window of the newest twenty-five would start dropping components once
 * a release had a few retries in it. Needs the `instructions (release, createdAt)`
 * composite index in both index files.
 */
export function useReleaseInstructions(
    version: string | undefined,
    max = RELEASE_ACTIVITY_WINDOW,
): Loadable<Array<InstructionDoc & { id: string }>> {
    return useQuery<InstructionDoc>(
        () =>
            query(
                collection(dbForRole('release'), COLLECTION.instructions),
                where(INSTRUCTION_FIELD.release, '==', version ?? '-'),
                orderBy(INSTRUCTION_FIELD.createdAt, 'desc'),
                limit(max),
            ),
        [version, max],
    );
}

/**
 * Where the release is, assembled once from every document that decides it.
 *
 * Both the home page and the app bar need the same answer, and the app bar needs it on
 * every page — an operator who left a compile running comes back to whichever tab was
 * open. Subscribing in one hook rather than in each consumer keeps the two from
 * disagreeing, and costs nothing extra: the SDK multiplexes identical listeners, so
 * the second caller joins the first one's snapshots.
 *
 * The component documents are read for the deploy counts but this release's
 * instructions are not: they only add the "compiling right now" states, which no
 * consumer of *this* hook shows, and they are the one query here that is per-release
 * and large. The table subscribes to them itself.
 */
export interface ReleasePosition {
    phase: Phase;
    /** The release either half is about, if any. */
    version: string | undefined;
    prepare: PrepareRelease | undefined;
    deploy: DeployRelease | undefined;
    rows: ComponentRow[];
    progress: DeployProgress;
    /** True until every document that decides the phase has been heard from. */
    loading: boolean;
}

export function useReleasePosition(): ReleasePosition {
    const prepareHost = useHost('prepare');
    const releaseHost = useHost('release');
    const prepareReleases = usePrepareReleases();
    const deployReleases = useDefaultReleases();
    const prepareInstructions = useRecentInstructions('prepare');
    const releaseInstructions = useRecentInstructions('release');
    const components = useComponents();

    const prepare = activePrepareRelease(prepareReleases.value);
    const deploy = activeDeployRelease(deployReleases.value);
    const phase = homePhase({
        prepare,
        deployReleases: deployReleases.value,
        prepareHost: prepareHost.value,
        releaseHost: releaseHost.value,
        prepareInstructions: prepareInstructions.value,
        releaseInstructions: releaseInstructions.value,
    });
    const version = deploy?.version ?? prepare?.version;
    const rows = componentRows({
        version: version ?? '',
        plan: deploy?.plan,
        components: components.value,
        acknowledgements: deploy?.acknowledgements,
        instructions: [],
    });

    return {
        phase,
        version,
        prepare,
        deploy,
        rows,
        progress: deployProgress(rows),
        loading: homeSourcesLoading({
            prepareHost: prepareHost.loading,
            releaseHost: releaseHost.loading,
            prepareReleases: prepareReleases.loading,
            deployReleases: deployReleases.loading,
        }),
    };
}

/**
 * The release system as the main flow sees it: one machine that is asleep or
 * awake, derived from both host documents and the phase (`lib/releaseSystem.ts`).
 *
 * Subscribed here rather than in each consumer so the page that gates on the state
 * and the bar that reports the sleep timer cannot disagree, and because the SDK
 * multiplexes the identical host listeners onto the ones the app already has.
 */
export function useReleaseSystem(phase: Phase): SystemView {
    const prepare = useHost('prepare');
    const release = useHost('release');
    // The seconds are the content while a VM is changing state — an operator
    // watching a wake is watching this number — and noise the rest of the time, so
    // the interval is picked from the hosts' own `status` rather than from the
    // presentation the tick then decides. A derived state cannot choose the clock it
    // is derived with.
    const changing = [prepare.value?.status, release.value?.status].some(
        (status) => status === 'starting' || status === 'stopping',
    );
    const now = useNow(changing ? LIVE_TICK_MS : CAPTION_TICK_MS);
    return systemView(phase, {
        prepare: {
            view: hostView(prepare.value, now, prepare.loading),
            expectedBootSeconds: hostInstance('prepare')?.expectedBootSeconds,
        },
        release: {
            view: hostView(release.value, now, release.loading),
            expectedBootSeconds: hostInstance('release')?.expectedBootSeconds,
        },
    });
}

/**
 * The prepare host's "is there anything to release?" summary. Only `host.sync`
 * on that host writes it, so the subscription is live in the sense that a new
 * answer appears the moment it is published — not in the sense that the answer
 * is continuously true. `lib/unreleased.ts` turns it into what the page shows.
 */
export function useUnreleased(): Loadable<UnreleasedDoc | undefined> {
    return useDoc<UnreleasedDoc>(dbPrepare, `${COLLECTION.main}/${MAIN_DOC.unreleased}`);
}

/**
 * Everything a button that issues one instruction kind needs: the create, and
 * the newest instruction of that kind reported back.
 *
 * The two belong together because creating an instruction changes nothing the
 * clicker can see — the host has to act — so a button without the report reads
 * as a dead button and gets clicked again. `disabledReason` is the caller's own
 * reason (a precondition it knows about); an instruction already in flight adds
 * its own.
 */
export interface InstructionAction {
    run: () => void;
    /** The create request is in the air (not the instruction — that is `activity`). */
    sending: boolean;
    /** The create itself failed: rejected by the rules, or the browser is offline. */
    error?: string;
    latest?: InstructionRow;
    activity?: ActivityLine;
    disabledReason?: string;
}

export function useInstructionAction(
    host: HostRole,
    kind: InstructionKind,
    opts: {
        params?: Record<string, unknown>;
        release?: string;
        disabledReason?: string;
        /**
         * Narrows which instruction of this kind the card reports on. Needed when a
         * kind covers several actions — `prepare.step` is one kind for five steps —
         * because reporting the newest of the *kind* would put another step's outcome
         * under this button.
         */
        match?: (row: InstructionRow) => boolean;
    } = {},
): InstructionAction {
    const operator = useOperator();
    const [sending, setSending] = useState(false);
    const [error, setError] = useState<string | undefined>(undefined);

    const hostDoc = useHost(host);
    const rows = useRecentInstructions(host).value;
    const latest = latestForAction(rows, kind, opts.release, opts.match);
    // Only a live instruction puts a seconds counter on the caller's card
    // ("Running for 1m 20s"); a finished one leaves a coarse "Succeeded 2m ago"
    // that does not need a re-render every second while the operator reads it.
    const now = useNow(latest && isInflightStatus(latest.status) ? LIVE_TICK_MS : CAPTION_TICK_MS);
    const activity =
        latest &&
        describeActivity(
            latest,
            {
                role: host,
                view: hostView(hostDoc.value, now, hostDoc.loading),
                expectedBootSeconds: hostInstance(host)?.expectedBootSeconds,
            },
            now,
        );

    const run = () => {
        setSending(true);
        setError(undefined);
        void createInstruction(dbForRole(host), host, kind, opts.params ?? {}, operator, opts.release).then((result) => {
            setSending(false);
            if (!result.ok) setError(result.error);
        });
    };

    return {
        run,
        sending,
        error,
        latest,
        activity,
        disabledReason: opts.disabledReason ?? (activity?.inflight ? INFLIGHT_DISABLED_REASON : undefined),
    };
}

/** One instruction from a host's database. */
export function useInstruction(role: HostRole, id: string): Loadable<(InstructionDoc & { id: string }) | undefined> {
    const { value, loading } = useDoc<InstructionDoc>(dbForRole(role), `${COLLECTION.instructions}/${id}`);
    return { value: value ? { id, ...value } : undefined, loading };
}

/** Fetch an archived artifact (log fallback, change-set file) as text. */
export function useArtifact(path: string | undefined): { text?: string; error?: string; loading: boolean } {
    const [state, setState] = useState<{ text?: string; error?: string; loading: boolean }>({ loading: !!path });
    useEffect(() => {
        if (!path) {
            setState({ loading: false });
            return;
        }
        let cancelled = false;
        setState({ loading: true });
        getBytes(storageRef(storage, path))
            .then((buf) => !cancelled && setState({ text: new TextDecoder().decode(buf), loading: false }))
            .catch((err) => !cancelled && setState({ error: storageErrorText(err), loading: false }));
        return () => {
            cancelled = true;
        };
    }, [path]);
    return state;
}
